import * as React from "react";
import * as apiSubscriptions from "../../../api/subscriptions";
import { Alert, AlertType } from "../../../components/alert";
import { Button, ButtonType } from "../../../components/button";
import { CheckBox } from "../../../components/checkbox";
import { ComboBox, ComboBoxType, DropDownTextItem, DROPDOWN_TEXT_ITEM_HEIGHT } from "../../../components/comboBox";
import { FilterCriteria } from "../../../components/filterCriteria";
import { FormGroup } from "../../../components/form";
import { InputSize } from "../../../components/input";
import { Loader, LoaderStyle } from "../../../components/loader";
import { ScrollBarVisibility, ScrollViewer } from "../../../components/scrollViewer";
import { TextBox } from "../../../components/textBox";
import { Window } from "../../../components/window";
import * as globals from "../../../globals";
import * as modelsGet from "../../../models/get";
import { ISubscriptionFilter } from "../../../models/get";
import * as utils from "../../../utils/utils";

enum Mode
{
    create,
    edit
}

enum SubscriberTypes {
    Group = "Group",
    User = "User",
    Cementery = "Cementery"
}

interface Subscriber {
    id?: number;
    name?: string;
    type?: SubscriberTypes;
}

interface IProperties
{
    /**
     * Die ID des zu öffnenden Datensatzes. (Ist leer wenn eine neuer Datensatz erstellt werden soll.)
     */
    subscriptionId?: number,

    /**
     * Wird aufgerufen sobald sich das Fenster schließt.
     */
    onClose?: (refresh?: boolean) => void
}

interface IState
{
    currentUser?: modelsGet.IUser;
    isLoading?: boolean;
    mode?: Mode;
    subscription?: modelsGet.ISubscription;
    templates?: modelsGet.ITemplate[];
    form?: {
        description?: string;
        subscriber?: string;
        template?: modelsGet.ITemplate;
        actionCreate?: boolean;
        actionChange?: boolean;
        actionDelete?: boolean;
        canceled?: boolean;
        filters?: ISubscriptionFilter[];
    },
    errors?: Object;
    alertMessage?: string;
    alertType?: AlertType;
}

export default class Dialog extends React.Component<IProperties, IState>
{
    private _initialState: IState;
    private _isSaveButtonDisabled: boolean;
    
    constructor(props: IProperties)
    {
        super(props);

        // Modus ermitteln.
        var mode: Mode = Mode.create;
        if (props.subscriptionId > 0)
        {
            mode = Mode.edit;
        }

        this.state = {
            isLoading: true,
            mode: mode,
            form: {},
            errors: {}
        };

        this.initAsync(props);
    }

    private async initAsync(props: IProperties)
    {
        if (this.state.mode === Mode.edit)
        {
            await this.initEditModeAsync(props);
        }
        else if (this.state.mode === Mode.create)
        {
            await this.initCreateModeAsync(props);
        }
    }

    private async initEditModeAsync(props: IProperties)
    {
        const currentUser: modelsGet.IUser = await globals.getCurrentUserAsync();
        const templates = await globals.getTemplatesAsync();
        const subscription = await apiSubscriptions.getSubscription(props.subscriptionId);

        // Werte setzen.
        this.setState((state: IState) =>
        {
            const newState: IState = {};
            newState.isLoading = false;
            newState.errors = {};
            newState.alertMessage = null;
            newState.alertType = null;

            newState.currentUser = currentUser;
            newState.subscription = subscription;
            newState.templates = templates.where(t => !t.is_list);

            newState.form = {
                subscriber: subscription.subscribers.select(s => s.subscriber_name).join(", "),
                description: subscription.description,
                template: templates.where(t => t.id === subscription.template_id).first() || {},
                actionCreate: subscription.action_created,
                actionChange: subscription.action_changed,
                actionDelete: subscription.action_deleted,
                canceled: subscription.subscribers.where(s => s.subscriber_type === "User" && s.subscriber === currentUser.id).select(s => s.canceled).first(),
                filters: newState.subscription.filters ? [...newState.subscription.filters] : []
            };

            if (currentUser.mortician_id > 0)
                newState.form.filters.forEach(f =>
                {
                    if (f.field === "UserId" || f.field === "Persons.Any(IsMortician == true And Id")
                        f.disabled = true
                });

            return newState;
        }, () => this._initialState = {
            ...this.state,
            form: {
                ...this.state.form
            }
        });
    }
    
    private async initCreateModeAsync(props: IProperties)
    {
        const currentUser: modelsGet.IUser = await globals.getCurrentUserAsync();
        const templates = await globals.getTemplatesAsync();
        const filters: ISubscriptionFilter[] = [];
        if (currentUser.mortician_id > 0)
        {
            filters.push({
                field: "UserId",
                operator: "==",
                value: `{"value":"[Ich]"}`,
                value_parsed: "[Ich]",
                disabled: true
            });

            filters.push({
                sentential_operator: "Or",
                field: "Persons.Any(IsMortician == true And Id",
                operator: "==",
                value: `{"value":"[Bestatter]"}`,
                value_parsed: "[Bestatter]",
                disabled: true
            });
        }

        this.setState(() =>
        {
            const newState: IState = {};
            newState.isLoading = false;
            newState.errors = {};
            newState.alertMessage = null;
            newState.alertType = null;

            newState.currentUser = currentUser;
            newState.subscription = {
                subscribers: [
                    {
                        subscriber: currentUser.id,
                        subscriber_type: "User",
                    }
                ],
                filters: filters
            };
            newState.templates = templates.where(t => !t.is_list);

            newState.form = {
                subscriber: currentUser.fullname,
                template: {},
                filters: [...newState.subscription.filters]
            };

            return newState;
        }, () => this._initialState = {
            ...this.state,
            form: {
                ...this.state.form
            }
        });
    }

    componentWillUpdate(nextProps: IProperties, nextState: IState)
    {
        this._isSaveButtonDisabled = true;

        if (this._initialState != null)
        {
            const { form } = this._initialState;

            if (form.description !== nextState.form.description)
                this._isSaveButtonDisabled = false;

            if (form.template.id !== nextState.form.template.id)
                this._isSaveButtonDisabled = false;

            if (form.actionCreate !== nextState.form.actionCreate)
                this._isSaveButtonDisabled = false;

            if (form.actionChange !== nextState.form.actionChange)
                this._isSaveButtonDisabled = false;

            if (form.actionDelete !== nextState.form.actionDelete)
                this._isSaveButtonDisabled = false;

            if (form.canceled !== nextState.form.canceled)
                this._isSaveButtonDisabled = false;

            if (!Object.equals(form.filters, nextState.form.filters))
                this._isSaveButtonDisabled = false;
        }
    }

    private onClosing = (refresh: boolean = false) =>
    {
        var closeDialog: boolean = true;

        if (!this._isSaveButtonDisabled)
            closeDialog = confirm("Es sind noch ungespeicherte Änderungen vorhanden. Wollen Sie den Dialog trotzdem schließen?");

        if (closeDialog)
        {
            const { onClose } = this.props;
            onClose && onClose(refresh);
            return true;
        }
    }

    private onSaveOrCreateClick = async () =>
    {
        try
        {
            this.setState({
                isLoading: true
            });

            const { currentUser, mode, subscription, form } = this.state;
            const isDisabled: boolean = subscription.subscribers.any(s => s.subscriber_type !== "User");

            if (!isDisabled)
            {
                var newData: modelsGet.ISubscription = {};
                newData.description = form.description;
                newData.subscribers = [{
                    subscriber: currentUser.id,
                    subscriber_type: "User",
                    canceled: form.canceled
                }];
                newData.action_created = form.actionCreate;
                newData.action_changed = form.actionChange;
                newData.action_deleted = form.actionDelete;
                newData.template_id = form.template && form.template.id;
                newData.created_at = subscription.created_at;
                newData.created_by = subscription.created_by;
                newData.changed_at = subscription.changed_at;
                newData.changed_by = subscription.changed_by;
                newData.filters = form.filters;

                if (mode === Mode.edit)
                {
                    await apiSubscriptions.put(subscription.id, newData);
                }
                else
                {
                    await apiSubscriptions.post(newData);
                }
            }
            else
            {
                await apiSubscriptions.putCancel(subscription.id, {
                    canceled: form.canceled
                });
            }

            // Fenster schließen.
            this._isSaveButtonDisabled = true;
            this.onClosing(true);
        }
        catch (e)
        {
            var errors = utils.getErrors(e);
            var alertMessage: string;

            if (errors != null)
            {
                for (let error in errors)
                {
                    switch (error)
                    {
                        case "error":
                        case "date_is_invalid":
                            alertMessage = errors[error];
                            break;
                        case "internal_server_error":
                            alertMessage = "Auf dem Server ist ein Fehler aufgetreten! Wenn der Fehler weiterhin auftritt, kontaktieren Sie Ihren Administrator oder Ihren Software Anbieter.";
                            break;
                    }
                }
            }

            this.setState(() =>
            {
                const newState: IState = {};
                newState.isLoading = false;
                newState.errors = errors;
                newState.alertMessage = alertMessage;
                newState.alertType = AlertType.error;
                return newState;
            });
        }
    }

    private onDeleteClick = async () =>
    {
        var deleteData = confirm("Wollen Sie den Datensatz wirklich löschen?");
        if (!deleteData)
            return;

        try
        {
            this.setState({
                isLoading: true
            });

            const { subscription } = this.state;

            await apiSubscriptions.remove(subscription.id);

            // Fenster schließen.
            this._isSaveButtonDisabled = true;
            this.onClosing(true);
        }
        catch (e)
        {
            var errors = utils.getErrors(e);
            var alertMessage: string;

            if (errors != null)
            {
                for (let error in errors)
                {
                    switch (error)
                    {
                        case "error":
                        case "date_is_invalid":
                            alertMessage = errors[error];
                            break;
                        case "internal_server_error":
                            alertMessage = "Auf dem Server ist ein Fehler aufgetreten! Wenn der Fehler weiterhin auftritt, kontaktieren Sie Ihren Administrator oder Ihren Software Anbieter.";
                            break;
                    }
                }
            }

            this.setState(() =>
            {
                const newState: IState = {};
                newState.isLoading = false;
                newState.errors = errors;
                newState.alertMessage = alertMessage;
                newState.alertType = AlertType.error;
                return newState;
            });
        }
    }

    private onControlTextChanged = (value: any[], originalValue: string[], name: string) =>
    {
        this.setState((state: IState) =>
        {
            state.form[name] = value[0];
            return state;
        });
    }

    private onComboBoxChanged = (selectedItem: any, formItemName: string) => {
        this.setState((state: IState) => {
            state.form[formItemName] = selectedItem;
            return state;
        });
    }

    private onCheckboxChanged = (e: React.ChangeEvent<HTMLInputElement>, name: string) =>
    {
        var target = e.target;
        var value = target.checked;

        this.setState((state: IState) => {
            state.form[name] = value;
            return state;
        });
    }

    private onNewFilterCriteriaClause = (index?: number) =>
    {
        const { form } = this.state;
        const newFilters = [...form.filters];
        const filter: ISubscriptionFilter = {
            id: -newFilters.length,
            value: "text " + (-newFilters.length)
        };
        const hasItems: boolean = newFilters.length > 0;

        if (hasItems && (index == null || index > 0))
        {
            filter.sentential_operator = "And";
        }
        else if (hasItems && index === 0)
        {
            newFilters[0].sentential_operator = "And";
        }

        if (index != null)
        {
            newFilters.splice(index, 0, filter);
        }
        else
        {
            newFilters.push(filter);
        }

        this.setState((state: IState) =>
        {
            state.form.filters = newFilters;
            return state;
        });
    }

    private onRemoveFilterCriteriaClause = (index: number) =>
    {
        const { form } = this.state;
        const newFilters = [...form.filters];
        newFilters.splice(index, 1);

        if (newFilters.length > 0 && index === 0)
            newFilters[0].sentential_operator = undefined;

        this.setState((state: IState) =>
        {
            state.form.filters = newFilters;
            return state;
        });
    }

    private onClauseChange = (item: ISubscriptionFilter) =>
    {
        const { form } = this.state;
        const newFilters = [...form.filters];

        for (var i = 0; i < newFilters.length; i++)
        {
            if (newFilters[i].id === item.id)
            {
                newFilters[i] = { ...item };
                break;
            }
        }

        this.setState((state: IState) =>
        {
            state.form.filters = newFilters;
            return state;
        });
    }

    private renderContent(): JSX.Element
    {
        const {
            mode,
            subscription,
            templates,
            form,
            errors,
            alertMessage,
            alertType
        } = this.state;

        const isDisabled: boolean = subscription.subscribers.any(s => s.subscriber_type !== "User");

        return (
            <div className="settings_dialog_content">
                <ScrollViewer
                    HorizontalScrollBarVisibility={ScrollBarVisibility.hidden}
                    VerticalScrollBarVisibility={ScrollBarVisibility.auto}
                    style={{
                        padding: 10
                    }}>
                    <FormGroup>
                        <TextBox
                            label="Beschreibung"
                            value={form.description}
                            size={InputSize.normal}
                            spellCheck={true}
                            autoFocus={true}
                            errorMessage={errors["Description"]}
                            onTextChange={(value, originalValue) => this.onControlTextChanged(value, originalValue, "description")}
                            isDisabled={isDisabled}
                        />
                        <TextBox
                            label="Abonnent"
                            value={form.subscriber}
                            size={InputSize.normal}
                            spellCheck={true}
                            autoFocus={true}
                            isDisabled={true}
                        />
                        <CheckBox
                            style={{
                                margin: "5px 0"
                            }}
                            label="Abgemeldet"
                            isChecked={form.canceled}
                            onCheckedChanged={(e) => this.onCheckboxChanged(e, "canceled")}
                        />
                    </FormGroup>
                    <FormGroup>
                        <label style={{
                            margin: "5px 0"
                        }}>Aktion</label>
                        <CheckBox
                            label="Erstellen"
                            isChecked={form.actionCreate}
                            onCheckedChanged={(e) => this.onCheckboxChanged(e, "actionCreate")}
                            isDisabled={isDisabled}
                        />
                        <CheckBox
                            label="Bearbeiten"
                            isChecked={form.actionChange}
                            onCheckedChanged={(e) => this.onCheckboxChanged(e, "actionChange")}
                            isDisabled={isDisabled}
                        />
                        <CheckBox
                            label="Löschen"
                            isChecked={form.actionDelete}
                            onCheckedChanged={(e) => this.onCheckboxChanged(e, "actionDelete")}
                            isDisabled={isDisabled}
                        />
                    </FormGroup>
                    <FormGroup>
                        <ComboBox
                            label="Dokument"
                            dataSource={templates}
                            dropDownMenuItemHeight={DROPDOWN_TEXT_ITEM_HEIGHT}
                            comboBoxType={ComboBoxType.dropDownList}
                            value={form.template.name}
                            selectedValue={form.template.id}
                            onSelectionChanged={(data) => this.onComboBoxChanged(data, "template")}
                            valueMember="id"
                            displayMember="name"
                            isDisabled={isDisabled}
                            hint="Wählen Sie ein Dokument aus welches an die E-Mail der Benachrichtigung angehängt werden soll.">
                            {
                                (item: modelsGet.ITemplate) => {
                                    return (
                                        <DropDownTextItem
                                            key={item.id}
                                            value={item.id}
                                            displayValue={item.name} />
                                    );
                                }
                            }
                        </ComboBox>
                        <FilterCriteria
                            label="Filterkriterien"
                            filters={form.filters}
                            onNewClause={this.onNewFilterCriteriaClause}
                            onRemoveClause={this.onRemoveFilterCriteriaClause}
                            onClauseChange={this.onClauseChange}
                            isDisabled={isDisabled}
                        />
                    </FormGroup>
                </ScrollViewer>
            </div>
        );
    }

    public render(): JSX.Element
    {
        const {
            isLoading,
            mode,
            subscription,
            alertMessage,
            alertType
        } = this.state;

        var icon: string = "loading";
        var title: string = "Daten werden geladen...";
        var buttonHeader: string = "";

        if (!isLoading)
        {
            if (mode === Mode.create)
            {
                icon = "plus";
                title = "Neue Benachrichtigungsregel erstellen";
                buttonHeader = "Erstellen";
            }
            else
            {
                icon = "pencil";
                title = `Benachrichtigungsregel "${subscription.description}" bearbeiten`;
                buttonHeader = "Speichern";
            }
        }

        return (
            <Window
                isOpen={true}
                titleIcon={icon}
                title={title}
                onClosing={this.onClosing}
                isMaximized={false}
                width={600}
                height={560}
                onEnterKeyUp={this.onSaveOrCreateClick}
                onEscapeKeyUp={() => this.onClosing(false)}>
                <div className="settings_dialog">
                    <Alert
                        style={utils.getGridChildStyle(1, 1, 1, 1)}
                        alertType={alertType}
                        isVisible={alertMessage ? true : false}
                        isClosable={true}
                        onHide={() => this.setState({ alertMessage: null, alertType: null })}>
                        {alertMessage}
                    </Alert>
                    {isLoading ? <Loader loaderStyle={LoaderStyle.style1} /> : this.renderContent()}
                    <div className="settings_dialog_actions">
                        {
                            mode === Mode.edit &&
                            <Button
                                text="Löschen"
                                type={ButtonType.danger}
                                onClick={this.onDeleteClick} />
                        }
                        <div style={{ flexGrow: 1 }}></div>
                        <Button
                            text={buttonHeader}
                            type={ButtonType.primary}
                            isDisabled={this._isSaveButtonDisabled}
                            onClick={this.onSaveOrCreateClick} />
                        <Button
                            text="Abbrechen"
                            type={ButtonType.default}
                            onClick={() => this.onClosing(false)} />
                    </div>
                </div>
            </Window>
        );
    }
}